import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ShoppingBag, Zap, ChevronLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import ScentRitual from "@/components/ScentRitual";
import ProductCard from "@/components/ProductCard";
import { StarRating, StockBadge, PriceTag, QtyStepper } from "@/components/ui-bits";
import { useLang } from "@/lib/i18n";

export const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { add } = useCart();
  const { t, pick } = useLang();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [reviewMsg, setReviewMsg] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    setQty(1);
    api.get(`/products/${slug}`)
      .then((d) => {
        setProduct(d.data);
        setReviews(d.data.reviews || []);
      })
      .catch(() => navigate("/winkel"));
  }, [slug]);

  if (!product) {
    return <div className="min-h-screen bg-obsidian pt-32 px-6"><div className="max-w-md aspect-[3/4] bg-ink animate-pulse" /></div>;
  }

  const soldOut = product.stock <= 0;
  const noPrice = !product.price || product.price <= 0;
  const style = product.image_style || "atelier";
  const boxed = style === "studio" || style === "promo";
  const frameBg = style === "studio" ? "bg-[#ECEAE3]" : "bg-ink";
  const imgClass = style === "studio" ? "object-contain p-10" : style === "promo" ? "object-contain p-6" : "object-cover";

  const addToCart = (goCheckout = false) => {
    add(product, qty);
    if (goCheckout) navigate("/checkout");
    else navigate("/winkelwagen");
  };

  const submitReview = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const d = await api.post(`/products/${slug}/reviews`, { rating, text });
      setReviews(d.data.reviews);
      setReviewMsg(t("product.thanks"));
      setText("");
      api.get(`/products/${slug}`).then((r) => setProduct(r.data));
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div className="px-6 sm:px-12 lg:px-20 pt-28 pb-24">
      <button data-testid="back-to-shop" onClick={() => navigate("/winkel")} className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-smoke hover:text-gold transition-colors mb-10">
        <ChevronLeft size={14} /> {t("product.back")}
      </button>

      <div className="grid lg:grid-cols-2 gap-12 xl:gap-20">
        <div className={`relative border border-gold/10 ${frameBg}`}>
          <img src={product.image_url} alt={pick(product, "model_fr") || product.model} className={`w-full aspect-square lg:aspect-[4/5] ${imgClass}`} />
        </div>

        <div>
          <div className="flex items-center gap-3">
            <Link to={`/winkel?brand=${product.brand}`} className="font-mono text-[10px] tracking-[0.35em] uppercase text-gold hover:text-gold-light transition-colors">{product.brand}</Link>
            <span className="h-px w-8 bg-gold/40" />
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke">{product.ml}</span>
          </div>

          <h1 className="mt-5 font-serif font-light text-cream text-3xl sm:text-4xl lg:text-5xl leading-tight">{pick(product, "model")}</h1>
          <p className="mt-4 font-serif italic text-gold/85 text-base sm:text-lg">{pick(product, "tagline")}</p>

          <div className="mt-5 flex items-center gap-4">
            <StarRating value={product.rating_avg || 0} />
            <span className="font-mono text-[10px] text-smoke">{product.rating_count > 0 ? `${product.rating_count} ${t("product.reviewCount")}` : t("product.noReviews")}</span>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-5">
            <PriceTag price={product.price} discountPrice={product.discount_price} className="text-2xl" />
            <StockBadge stock={product.stock} />
          </div>
          <p className="mt-2 font-mono text-[10px] tracking-[0.2em] uppercase text-smoke">
            {noPrice ? t("product.onAsk") : t("product.vat")}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            {!noPrice && <QtyStepper value={qty} onChange={setQty} max={Math.max(1, product.stock)} testid="product-qty" />}
            <button
              data-testid="add-to-cart-button"
              onClick={() => addToCart(false)}
              disabled={soldOut || noPrice}
              className="flex items-center gap-3 bg-gold text-obsidian px-8 py-4 font-mono text-xs tracking-[0.3em] uppercase hover:bg-gold-light transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ShoppingBag size={15} /> {t("product.add")}
            </button>
            <button
              data-testid="buy-now-button"
              onClick={() => addToCart(true)}
              disabled={soldOut || noPrice}
              className="flex items-center gap-3 border border-cream/25 text-cream px-8 py-4 font-mono text-xs tracking-[0.3em] uppercase hover:border-gold hover:text-gold transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Zap size={15} /> {t("product.buyNow")}
            </button>
          </div>

          <div className="mt-10">
            <ScentRitual product={product} />
          </div>

          <div className="mt-10">
            <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/85 mb-4">{t("product.about")}</h2>
            <p className="text-sm leading-relaxed text-mist">{pick(product, "description")}</p>
          </div>
        </div>
      </div>

      <div className="mt-24 grid lg:grid-cols-2 gap-16">
        <div>
          <h2 className="font-serif font-light uppercase text-cream text-2xl sm:text-3xl mb-8">
            {t("product.reviewsTitle")} <span className="italic normal-case text-gold">({reviews.length})</span>
          </h2>
          {reviews.length === 0 && (
            <p className="text-sm text-mist">{t("product.noReviews")} — {t("product.first")}</p>
          )}
          <div className="space-y-6">
            {reviews.map((r) => (
              <div key={r.review_id} data-testid="review-item" className="border-t border-gold/10 pt-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-cream">{r.user_name}</span>
                  <StarRating value={r.rating} />
                </div>
                <p className="mt-3 text-sm text-mist leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-serif font-light uppercase text-cream text-2xl sm:text-3xl mb-8">{t("product.writeTitle1")} <span className="italic normal-case text-gold">{t("product.writeTitle2")}</span></h2>
          {user ? (
            <form data-testid="review-form" onSubmit={submitReview} className="border border-gold/15 bg-ink p-6 space-y-5">
              <div>
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke block mb-3">{t("product.yourScore")}</span>
                <StarRating value={rating} onRate={setRating} size={22} />
              </div>
              <textarea
                data-testid="review-text-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                required
                minLength={3}
                placeholder={t("product.placeholder")}
                className="w-full bg-obsidian border border-gold/15 px-4 py-3 text-sm text-cream placeholder:text-smoke focus:outline-none focus:border-gold/50"
              />
              {error && <p data-testid="review-error" className="text-xs text-red-400">{error}</p>}
              {reviewMsg && <p data-testid="review-success" className="text-xs text-emerald-300">{reviewMsg}</p>}
              <button type="submit" data-testid="review-submit-button" className="bg-gold text-obsidian font-mono text-[10px] tracking-[0.3em] uppercase px-8 py-3.5 hover:bg-gold-light transition-colors">
                {t("product.send")}
              </button>
            </form>
          ) : (
            <p className="text-sm text-mist">
              <button onClick={() => navigate("/inloggen")} data-testid="review-login-link" className="text-gold underline underline-offset-4">{t("nav.login")}</button> {t("product.loginToReview")}
            </p>
          )}
        </div>
      </div>

      {product.related?.length > 0 && (
        <div className="mt-24">
          <h2 className="font-serif font-light uppercase text-cream text-2xl sm:text-3xl mb-10">
            {t("product.related")} <span className="italic normal-case text-gold">{t("product.related2")}</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {product.related.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
